import React, { useMemo, useState, useRef } from "react";
import { IconButton, Tooltip, Box, TextField } from "@mui/material";
import { ArrowDropDown, Close, KeyboardArrowDown } from "@mui/icons-material";
import SearchableSelectModal, {
  SelectableOption,
} from "../searchable-select-modal";
import { deepPurple, grey } from "@mui/material/colors";

const sxStyles = {
  textField: {
    cursor: "pointer",
    [`& .MuiInputBase-input`]: {
      cursor: "pointer",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: grey[800] + " !important",
      caretColor: grey[800],
      "::placeholder": {
        color: grey[600] + " !important",
        opacity: 1,
      },
    },
    [`& .MuiInputLabel-root`]: {
      pointerEvents: "none",
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled)::before": {
      borderBottomColor: deepPurple[800],
    },
    "& .MuiInput-underline:after": {
      borderBottomColor: deepPurple[800],
    },
    "&.Mui-focused .MuiInputBase-input": {
      color: grey[800] + " !important",
    },
    "&.Mui-focused .MuiInputBase-input::placeholder": {
      color: grey[600] + " !important",
      opacity: 1,
    },
  },
  outlined: {
    background: "white",
    "& .MuiOutlinedInput-root": {
      height: "2.1875rem",
      fontSize: "0.875rem",
      cursor: "pointer",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: deepPurple[800],
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: deepPurple[800],
    },
    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
    "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
      transform: "translate(14px, 8px) scale(1)",
    },
  },
};

interface FilterOption extends SelectableOption {
  id: string;
  label: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selectedValues?: string[];
  onChange: (values: string[]) => void;
  onReset: () => void;
  variant?: "standard" | "outlined";
}

// Stable, so an unset filter keeps the memoized selection below.
const NO_SELECTION: string[] = [];

// Keys that open the popover from the focused field, as they do a select.
const OPEN_KEYS = ["Enter", " ", "ArrowDown"];

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selectedValues = NO_SELECTION,
  onChange,
  onReset,
  variant = "standard",
}) => {
  const outlined = variant === "outlined";
  const [isOpen, setIsOpen] = useState(false);
  const [openedWithKeyboard, setOpenedWithKeyboard] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Convert string options to SelectableOptions
  const selectableOptions: FilterOption[] = options.map((option) => ({
    id: option,
    label: option,
  }));

  // The popover resets its unapplied ticks whenever this array changes, so it
  // changes only with the saved selection, not when a poll brings new options.
  const selectedOptions: FilterOption[] = useMemo(
    () => selectedValues.map((value) => ({ id: value, label: value })),
    [selectedValues]
  );

  const displayText =
    selectedValues.length > 0 ? selectedValues.join(", ") : "";
  const isLongText = displayText.length > 16;
  const selectedDisplayText = isLongText
    ? `${selectedValues.length} selected`
    : displayText;

  const handleMouseEnter = () => {
    if (isLongText) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // The popover doesn't restore focus: a keyboard user goes back to the field,
  // and focus is dropped after a click, as before.
  const settleFocus = () =>
    requestAnimationFrame(() => {
      if (openedWithKeyboard) {
        inputRef.current?.focus();
      } else {
        inputRef.current?.blur();
      }
    });

  const handleSave = (selections: FilterOption[]) => {
    const values = selections.map((s) => s.id);
    onChange(values);
    setIsOpen(false);
    settleFocus();
  };

  const handleCancel = () => {
    setIsOpen(false);
    settleFocus();
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReset();
  };

  const openPopover = (withKeyboard: boolean) => {
    setOpenedWithKeyboard(withKeyboard);
    setIsOpen(true);
  };

  const handleFieldClick = () => {
    openPopover(false);
  };

  const handleFieldKeyDown = (event: React.KeyboardEvent) => {
    if (OPEN_KEYS.includes(event.key)) {
      event.preventDefault();
      openPopover(true);
    }
  };

  return (
    <>
      <Tooltip open={showTooltip} title={displayText} arrow placement="top">
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ display: "flex", width: "100%", alignItems: "center" }}
        >
          <TextField
            ref={fieldRef}
            inputRef={inputRef}
            label={label}
            value={selectedDisplayText}
            placeholder={
              selectedDisplayText ? "" : `Select ${label.toLowerCase()}`
            }
            variant={variant}
            size={outlined ? "small" : undefined}
            fullWidth
            onClick={handleFieldClick}
            slotProps={{
              inputLabel: {
                shrink: selectedDisplayText.length > 0,
                sx: {
                  color: grey[600] + " !important",
                  "&.Mui-focused": {
                    color: grey[800] + " !important",
                  },
                },
              },
              input: {
                readOnly: true,
                onKeyDown: handleFieldKeyDown,
                endAdornment:
                  selectedValues.length > 0 ? (
                    <IconButton
                      size="small"
                      onClick={handleReset}
                      sx={{
                        padding: "4px",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  ) : outlined ? (
                    <KeyboardArrowDown
                      sx={{ color: grey[600], pointerEvents: "none" }}
                    />
                  ) : (
                    <ArrowDropDown
                      sx={{
                        color: grey[600],
                        pointerEvents: "none",
                      }}
                    />
                  ),
              },
              // A text input can't take the button role, so it's a combobox
              // that opens the popover's dialog.
              htmlInput: {
                role: "combobox",
                "aria-haspopup": "dialog",
                "aria-expanded": isOpen,
              },
            }}
            sx={
              outlined
                ? [sxStyles.textField, sxStyles.outlined]
                : sxStyles.textField
            }
          />
        </Box>
      </Tooltip>

      <SearchableSelectModal
        open={isOpen}
        anchorElement={fieldRef.current}
        options={selectableOptions}
        selectedValues={selectedOptions}
        onSave={handleSave}
        onCancel={handleCancel}
        onClose={handleCancel}
        placeholder={`Search for ${label.toLowerCase()}`}
        multiple={true}
        width="23rem"
        focusOnOpen={openedWithKeyboard}
      />
    </>
  );
};

export default MultiSelectFilter;
